/**
 * @voltxampere/web — Holographic formula overlay store (Sprint 56b)
 *
 * Watches `currentCircuit` + `selectedNodes` and runs the engine
 * formula detector after a 200 ms debounce. The active match (or
 * null) lives on `currentFormulaMatch`; the overlay component
 * renders accordingly.
 *
 * Open / close toggle (`formulaOverlayOpen`) is user-controlled —
 * the chip-style indicator in the simulator pane can disable the
 * overlay even when a match is in hand.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect, signal } from "@preact/signals-core";
import { detectFormulas, type MatchResult } from "@voltxampere/engine";

import { currentCircuit } from "./circuit.js";
import { selectedNodes } from "./canvas.js";

export const DEFAULT_FORMULA_DEBOUNCE_MS = 200;

export const currentFormulaMatch = signal<MatchResult | null>(null);
export const formulaDetectionDurationMs = signal<number | null>(null);
/** Total matches surfaced by the detector (overlay shows the best). */
export const formulaMatchCount = signal<number>(0);
/** When true the overlay component renders. User-controlled. */
export const formulaOverlayOpen = signal<boolean>(true);

export function toggleFormulaOverlay(): void {
  formulaOverlayOpen.value = !formulaOverlayOpen.value;
}

interface BootFormulaOverlayOpts {
  readonly debounceMs?: number;
}

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let activeUnsubscribe: (() => void) | null = null;

export function bootFormulaOverlay(opts: BootFormulaOverlayOpts = {}): () => void {
  if (activeUnsubscribe) return activeUnsubscribe;
  const debounceMs = opts.debounceMs ?? DEFAULT_FORMULA_DEBOUNCE_MS;

  let firstRun = true;
  const dispose = effect(() => {
    const circuit = currentCircuit.value;
    const selection = selectedNodes.value;
    if (firstRun) {
      firstRun = false;
      if (circuit !== null) runOnce(circuit, selection);
      return;
    }
    if (circuit === null) {
      currentFormulaMatch.value = null;
      formulaMatchCount.value = 0;
      return;
    }
    schedule(circuit, selection, debounceMs);
  });

  activeUnsubscribe = (): void => {
    dispose();
    if (pendingTimer !== null) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    activeUnsubscribe = null;
    firstRun = true;
  };
  return activeUnsubscribe;
}

function schedule(
  circuit: NonNullable<ReturnType<typeof currentCircuit.peek>>,
  selection: readonly string[],
  debounceMs: number,
): void {
  if (pendingTimer !== null) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    runOnce(circuit, selection);
  }, debounceMs);
}

function runOnce(
  circuit: NonNullable<ReturnType<typeof currentCircuit.peek>>,
  selection: readonly string[],
): void {
  const selectedSet = selection.length > 0 ? new Set(selection) : undefined;
  const report = detectFormulas(circuit, selectedSet);
  currentFormulaMatch.value = report.best;
  formulaMatchCount.value = report.matches.length;
  formulaDetectionDurationMs.value = report.durationMs;
}

/** Test helper — full reset. */
export function resetFormulaOverlayForTests(): void {
  activeUnsubscribe?.();
  activeUnsubscribe = null;
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  currentFormulaMatch.value = null;
  formulaDetectionDurationMs.value = null;
  formulaMatchCount.value = 0;
  formulaOverlayOpen.value = true;
}
